import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of, Subscription } from 'rxjs';
import {
  CONTACT_DETAIL_CONTAINER,
  COMMERCIAL_CONTACT_DETAIL_CONTAINER,
  COMMERCIAL_FACILITY_DETAIL_CONTAINER,
  COMMERCIAL_FACILITY_SEARCH_CONTAINER,
  FACILITY_DETAIL_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  DELETE_COMMERCIAL_FACILITY,
  NEW_COMMERCIAL_FACILITY,
} from 'src/app/constants/commercial-constants';
import {
  VIEW_AUDIT,
  VIEW_HISTORICAL_RECORD,
} from 'src/app/constants/action-constants';
import { CLAIMS } from 'src/app/constants/auth-constants';
import {
  getDirtyValues,
  toSelectItems,
} from 'src/app/core/functions/common-functions';
import {
  buildHistoryTabId,
  historyVersionSuffix,
  HistoryTabReference,
  parseHistoryTabId,
} from 'src/app/core/functions/history-tab-functions';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  CommercialFacility,
  CommercialFacilityHistorySummary,
} from 'src/app/model/commercial-services/commercial-facility';
import { CommercialFacilityUpdateDto } from 'src/app/model/commercial-services/commercial-facility-update-dto';
import { AOR } from 'src/app/model/locations/aor';
import { Facility } from 'src/app/model/locations/facility';
import { CommercialContact } from 'src/app/model/commercial-services/commercial-contact';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { CommercialContactApiService } from 'src/app/services/api/commercial-contact-api.service';
import { CommercialFacilityBuilderService } from 'src/app/services/builder/commercial-facility-builder.service';
import { CommercialFacilityTableService } from 'src/app/services/table/commercial-facility-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { HistoryVersionDialogComponent } from 'src/app/core/containers/history-version-dialog/history-version-dialog.component';
import {
  CommercialFacilityMessageKeys,
  ValidationMessageEntry,
} from 'src/app/core/type-helpers/validation-msg';
import { CommercialFacilityFormComponent } from '../../components/commercial-facility-form/commercial-facility-form.component';
import { CommercialFacilityContactsComponent } from '../../components/commercial-facility-contacts/commercial-facility-contacts.component';
import { CommercialFacilityNotepadComponent } from '../../components/commercial-facility-notepad/commercial-facility-notepad.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

type FacilityLookupData = {
  companies: Array<{ id: string; name: string }>;
  facilityTypes: Array<{ id: string; name: string }>;
  operatingAreas: Array<{ id: string; name: string }>;
  balancingTypes: Array<{ id: string; name: string }>;
  aors: AOR[];
  commercialContacts: CommercialContact[];
};

@Component({
  selector: 'app-commercial-facility-details-container',
  templateUrl: './commercial-facility-details-container.component.html',
  styleUrls: ['./commercial-facility-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ButtonDirective,
    ScrollPanel,
    Select,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    CommercialFacilityFormComponent,
    CommercialFacilityContactsComponent,
    CommercialFacilityNotepadComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class CommercialFacilityDetailsContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _fb = inject(UntypedFormBuilder);
  protected _commercialFacilityApi = inject(CommercialFacilityApiService);
  protected _commercialContactApi = inject(CommercialContactApiService);
  protected _commercialFacilityBuilder = inject(
    CommercialFacilityBuilderService
  );
  protected _commercialFacilityTableService = inject(
    CommercialFacilityTableService
  );
  protected _authApi = inject(AuthApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _dialogService = inject(DialogService);
  private readonly _injector = inject(Injector);
  private readonly notepadScrollPanel =
    viewChild<ScrollPanel>('notepadScrollPanel');

  readonly facility = signal<CommercialFacility | null>(null);
  readonly saving = signal(false);
  readonly canEdit = signal(false);
  readonly canCreate = signal(false);
  readonly companyOptions = signal<SelectItem[]>([]);
  readonly availableFacilityTypes = signal<SelectItem[]>([]);
  readonly availableOperatingAreas = signal<SelectItem[]>([]);
  readonly availableMarketAreas = signal<SelectItem[]>([]);
  readonly aorOptions = signal<SelectItem[]>([]);
  readonly availableBalancingTypes = signal<SelectItem[]>([]);
  readonly availableMCFacilities = signal<SelectItem[]>([]);
  readonly commercialContacts = signal<CommercialContact[]>([]);
  readonly canViewCommercialContacts = signal(false);
  readonly canViewGcContacts = signal(false);
  readonly canViewMcFacilities = signal(false);
  readonly aors = signal<AOR[]>([]);
  readonly actions = signal<SelectItem[]>([]);
  readonly selectedAction = signal<string | null>(null);
  readonly isHistoricalRecord = signal(false);
  readonly formState = signal<UntypedFormGroup | undefined>(undefined);
  readonly formDirty = signal(false);
  readonly isLoading = signal(true);
  private facilitiesSub?: Subscription;
  validationMessages?: Record<
    CommercialFacilityMessageKeys,
    ValidationMessageEntry
  >;

  ngOnInit(): void {
    this.setupTabClosingSubscription();
    this.initialize();
  }

  initialize(): void {
    const validations =
      this._commercialFacilityBuilder.buildCommercialFacilityValidationMessages();
    this.validationMessages = validations.messages;

    const reference = parseHistoryTabId(this.id);
    this.isHistoricalRecord.set(reference.version !== undefined);
    this.canEdit.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .EDIT_COMMERCIAL_FACILITIES
      )
    );
    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .CREATE_COMMERCIAL_FACILITIES
      )
    );

    this.canViewGcContacts.set(
      this._authApi.doesUserHaveClaim(CLAIMS.CONTACTS.CONTACTS.VIEW_CONTACTS)
    );
    this.canViewMcFacilities.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.LOCATIONS.FACILITIES.VIEW_FACILITIES
      )
    );

    this.loadLookupData(reference);
  }

  private loadLookupData(reference: HistoryTabReference): void {
    // The contacts query requires its own claim; users without it get no options.
    const canViewCommercialContacts = this._authApi.doesUserHaveClaim(
      CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.VIEW_COMMERCIAL_CONTACTS
    );
    this.canViewCommercialContacts.set(canViewCommercialContacts);

    forkJoin({
      companies: this._commercialFacilityApi.getCommercialCompanies(),
      facilityTypes: this._commercialFacilityApi.getCommercialFacilityTypes(),
      operatingAreas: this._commercialFacilityApi.getCommercialOperatingAreas(),
      balancingTypes: this._commercialFacilityApi.getCommercialBalancingTypes(),
      aors: this._aorApi.getAORs(null),
      commercialContacts: canViewCommercialContacts
        ? this._commercialContactApi.getCommercialContacts()
        : of([]),
    }).subscribe({
      next: (lookups) => {
        this.setLookupOptions(lookups);
        this.loadFacility(reference);
      },
      error: (error) => this.handleLoadError(error),
    });
  }

  private setLookupOptions(lookups: FacilityLookupData): void {
    const choose = this._translateService.instant('COMMON.LABEL.CHOOSE');
    this.companyOptions.set(toSelectItems(lookups.companies, choose));
    this.availableFacilityTypes.set(
      toSelectItems(lookups.facilityTypes, choose)
    );
    this.availableOperatingAreas.set(
      toSelectItems(lookups.operatingAreas, choose)
    );
    this.availableBalancingTypes.set(
      toSelectItems(lookups.balancingTypes, choose)
    );
    this.aors.set(lookups.aors);
    this.aorOptions.set(toSelectItems(lookups.aors, choose));
    this.commercialContacts.set(lookups.commercialContacts);
  }

  /** Uses the app-wide cached facility list, which re-emits as it refreshes. */
  private loadMCFacilities(): void {
    this.facilitiesSub?.unsubscribe();
    this.facilitiesSub = this._facilityApi
      .getFacilities()
      .subscribe((facilities) => this.setMCFacilityOptions(facilities));
  }

  private setMCFacilityOptions(facilities: Facility[]): void {
    const selectedId = this.facility()?.mcFacilityId;
    this.availableMCFacilities.set(
      facilities
        .filter(
          (mcFacility) => !mcFacility.hidden || mcFacility.id === selectedId
        )
        .map((mcFacility) => ({
          label: [mcFacility.facilityName, mcFacility.facilityUid]
            .filter(Boolean)
            .join(' - '),
          value: mcFacility.id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label))
    );
  }

  private loadFacility(reference: HistoryTabReference): void {
    if (reference.id && reference.id !== NEW_COMMERCIAL_FACILITY) {
      this._commercialFacilityApi
        .getCommercialFacility(reference.id, reference.version)
        .subscribe({
          next: (facility) => {
            if (!facility) {
              this.handleLoadError(
                new Error('Commercial facility was not found')
              );
              return;
            }
            this.facility.set(facility);
            this.loadMCFacilities();
            this.loadMarketAreas(facility.operatingArea?.id ?? null, () =>
              this.setupScreen()
            );
          },
          error: (error) => this.handleLoadError(error),
        });
      return;
    }

    this.facility.set(
      this._commercialFacilityBuilder.buildNewCommercialFacility()
    );
    this._tabService.updateActiveTabLabel(
      this._translateService.instant(
        'COMMERCIAL_SERVICES.LABEL.NEW_COMMERCIAL_FACILITY'
      )
    );
    this.buildForm();
    this.loadMCFacilities();
    this.isLoading.set(false);
  }

  private loadMarketAreas(
    operatingAreaId: string | null,
    onComplete: () => void
  ): void {
    if (!operatingAreaId) {
      this.availableMarketAreas.set([]);
      onComplete();
      return;
    }

    this._commercialFacilityApi
      .getCommercialMarketAreas(operatingAreaId)
      .subscribe({
        next: (marketAreas) => {
          this.availableMarketAreas.set(
            toSelectItems(
              marketAreas,
              this._translateService.instant('COMMON.LABEL.CHOOSE')
            )
          );
          onComplete();
        },
        error: (error) => {
          this.availableMarketAreas.set([]);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'COMMERCIAL_SERVICES.MESSAGES.ERROR.LOAD_MARKET_AREAS',
          });
          console.error(error);
          onComplete();
        },
      });
  }

  private setupScreen(): void {
    const facility = this.facility();
    if (!facility) {
      this.handleLoadError(new Error('Commercial facility object is null'));
      return;
    }

    this.setTabLabel(facility);
    this.updateActionsList();
    this.buildForm();
    this.isLoading.set(false);
  }

  private setTabLabel(facility: CommercialFacility): void {
    const tab = this._tabService.getTabById(this.id);
    if (!tab) {
      return;
    }
    const label = this._translateService.instant(
      'COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_FACILITY'
    );
    const suffix = historyVersionSuffix(parseHistoryTabId(this.id).version);
    this._tabService.updateTabLabel(
      tab,
      `${label}: ${facility.facilityName}${suffix}`
    );
  }

  buildForm(): void {
    const facility = this.facility();
    if (!facility) {
      return;
    }

    const form = this._commercialFacilityBuilder.buildForm(this._fb, facility);
    this.form = form;
    this.formState.set(form);

    const writeAccess =
      !this.isHistoricalRecord() &&
      (facility.id ? this.canEdit() : this.canCreate());
    if (!writeAccess) {
      form.disable();
    }

    this.formDirty.set(form.dirty);
    this.valueSub = form.valueChanges.subscribe(() => {
      this.formDirty.set(form.dirty);
      if (form.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  saveFacility(): void {
    const facility = this.facility();
    if (
      this.isHistoricalRecord() ||
      !facility ||
      (facility.id ? !this.canEdit() : !this.canCreate())
    ) {
      this.showNoPermissionError('COMMON.LABEL.BUTTONS.SAVE');
      return;
    }

    if (!this.form?.valid) {
      this._logAndMessage.translateToErrorAlert({
        bodyKey: 'COMMON.MESSAGES.ERROR.INVALID_FORM',
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
      return;
    }

    this.saving.set(true);
    const raw = this.form.getRawValue();
    if (!raw.id) {
      this.createFacility(
        this._commercialFacilityBuilder.buildCreateObject(raw)
      );
      return;
    }

    this.updateFacility(
      this._commercialFacilityBuilder.buildUpdateStatement(
        facility,
        getDirtyValues(this.form)
      )
    );
  }

  createFacility(input: CommercialFacilityUpdateDto): void {
    this._commercialFacilityApi.createCommercialFacility(input).subscribe({
      next: (facility) => {
        this.facility.set(facility);
        this._commercialFacilityBuilder.updateForm(
          this.form!,
          facility,
          this.canEdit()
        );
        this.updateActionsList();
      },
      error: (error) => {
        console.error(error);
        this.saving.set(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey:
            'COMMERCIAL_SERVICES.MESSAGES.ERROR.CREATE_COMMERCIAL_FACILITY',
        });
      },
      complete: () => {
        // Re-key the existing tab instead of replacing it, so nothing reloads.
        const facility = this.facility()!;
        this._tabService.updateTabId(this.id, facility.id);
        this.id = facility.id;
        this.setTabLabel(facility);
        this.finishSave();
      },
    });
  }

  updateFacility(update: CommercialFacilityUpdateDto): void {
    const facility = this.facility();
    if (!facility || !this.canEdit()) {
      this.showNoPermissionError('COMMON.LABEL.BUTTONS.SAVE');
      this.saving.set(false);
      return;
    }

    this._commercialFacilityApi
      .updateCommercialFacility(facility.id, update)
      .subscribe({
        next: (updatedFacility) => {
          this.facility.set(updatedFacility);
          this._commercialFacilityBuilder.updateForm(
            this.form!,
            updatedFacility,
            this.canEdit()
          );
        },
        error: (error) => {
          console.error(error);
          this.saving.set(false);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.UPDATE_COMMERCIAL_FACILITY',
          });
        },
        complete: () => this.finishSave(),
      });
  }

  /**
   * Notes save independently of the facility form, but each change bumps the facility
   * version. Take only the notes and audit fields from the server so unsaved form
   * edits survive and the next save sends a current expectedVersion.
   */
  onNotesChanged(): void {
    const facility = this.facility();
    if (!facility?.id) {
      return;
    }

    this._commercialFacilityApi.getCommercialFacility(facility.id).subscribe({
      next: (latest) => {
        if (!latest) {
          return;
        }
        this.facility.set({
          ...(this.facility() ?? facility),
          Notes: latest.Notes,
          version: latest.version,
          updatedAt: latest.updatedAt,
          updater: latest.updater,
        });
        this.form
          ?.get('version')
          ?.setValue(latest.version, { emitEvent: false });
        afterNextRender(() => this.notepadScrollPanel()?.scrollTop(0), {
          injector: this._injector,
        });
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'COMMERCIAL_SERVICES.MESSAGES.ERROR.GET_COMMERCIAL_FACILITY',
        });
      },
    });
  }

  private finishSave(): void {
    this.formDirty.set(false);
    this.saving.set(false);
    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });
  }

  openCommercialContact(contactId: string): void {
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      contactId
    );
    this._tabService.openTab(tab);
  }

  openGcContact(contactId: string): void {
    if (!this.canViewGcContacts()) {
      this.showNoPermissionError('COMMERCIAL_SERVICES.LABEL.OPEN_CONTACT');
      return;
    }
    const tab = this._tabService.buildNewTab(
      CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      contactId
    );
    this._tabService.openTab(tab);
  }

  openMcFacility(facilityId: string): void {
    if (!this.canViewMcFacilities()) {
      this.showNoPermissionError('COMMERCIAL_SERVICES.LABEL.OPEN_MC_FACILITY');
      return;
    }
    const tab = this._tabService.buildNewTab(
      FACILITY_DETAIL_CONTAINER,
      true,
      undefined,
      facilityId
    );
    this._tabService.openTab(tab);
  }

  onOperatingAreaChange(operatingAreaId: string | null): void {
    const marketAreaControl = this.form?.get('marketAreaId');
    marketAreaControl?.reset(null, { emitEvent: false });
    this.availableMarketAreas.set([]);

    if (operatingAreaId) {
      this.loadMarketAreas(operatingAreaId, () => undefined);
    }
  }

  updateActionsList(): void {
    const facility = this.facility();
    if (!facility?.id || this.isHistoricalRecord() || facility.deletedAt) {
      this.actions.set([]);
      return;
    }

    const actions: SelectItem[] = [
      {
        label: this._translateService.instant('COMMON.LABEL.SELECT_ACTION'),
        value: null,
      },
    ];
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .VIEW_COMMERCIAL_FACILITIES
      )
    ) {
      actions.push(
        {
          label: this._translateService.instant('COMMON.LABEL.VIEW_AUDIT_INFO'),
          value: VIEW_AUDIT,
        },
        {
          label: this._translateService.instant(
            'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
          ),
          value: VIEW_HISTORICAL_RECORD,
        }
      );
    }
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_FACILITIES
          .DELETE_COMMERCIAL_FACILITIES
      )
    ) {
      actions.push({
        label: this._translateService.instant(
          'COMMERCIAL_SERVICES.LABEL.DELETE_FACILITY'
        ),
        value: DELETE_COMMERCIAL_FACILITY,
      });
    }
    this.actions.set(actions);
  }

  actionEvent(event: SelectChangeEvent, select?: Select): void {
    const action = event.value;
    // Select.clear() re-emits onChange with null; ignore it to avoid recursion.
    if (!action) {
      return;
    }
    this.selectedAction.set(null);
    if (action === VIEW_AUDIT) {
      const history$ =
        this._commercialFacilityApi.getCommercialFacilityHistories(
          this.facility()!.id
        );
      this._dialogService.open(AuditDialogContainerComponent, {
        dismissableMask: true,
        modal: true,
        closable: true,
        width: '25%',
        data: { history: history$ },
      });
    } else if (action === VIEW_HISTORICAL_RECORD) {
      this.viewHistoricalRecord();
    } else if (action === DELETE_COMMERCIAL_FACILITY) {
      this.deleteCommercialFacility();
    }
    select?.clear();
  }

  private viewHistoricalRecord(): void {
    const facility = this.facility()!;
    this._commercialFacilityApi
      .getCommercialFacilityHistories(facility.id)
      .subscribe({
        next: (histories: CommercialFacilityHistorySummary[]) => {
          this._dialogService
            .open(HistoryVersionDialogComponent, {
              header: this._translateService.instant(
                'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
              ),
              modal: true,
              closable: true,
              dismissableMask: true,
              data: {
                versions: histories.map((history) => ({
                  label: 'V' + history.version,
                  value: history.version,
                })),
              },
            })
            ?.onClose.subscribe((version?: number) => {
              if (version !== undefined) {
                this.openHistoricalVersion(facility.id, version);
              }
            });
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.AUDIT_INFO',
          });
        },
      });
  }

  private openHistoricalVersion(facilityId: string, version: number): void {
    this._commercialFacilityApi
      .getCommercialFacility(facilityId, version)
      .subscribe({
        next: (historicalFacility) => {
          if (!historicalFacility) {
            return;
          }
          const tab = this._tabService.buildNewTab(
            COMMERCIAL_FACILITY_DETAIL_CONTAINER,
            true,
            `${historicalFacility.facilityName}${historyVersionSuffix(version)}`,
            buildHistoryTabId(facilityId, version)
          );
          this._tabService.openTab(tab);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'COMMON.MESSAGES.ERROR.AUDIT_INFO',
          });
        },
      });
  }

  private deleteCommercialFacility(): void {
    const facility = this.facility();
    if (!facility) {
      return;
    }

    this._confirmationService.confirm({
      header: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.HEADERS.DELETE_FACILITY'
      ),
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this._translateService.instant('COMMON.LABEL.BUTTONS.YES'),
      rejectLabel: this._translateService.instant('COMMON.LABEL.BUTTONS.NO'),
      message: this._translateService.instant(
        'COMMERCIAL_SERVICES.MESSAGES.CONFIRMATION.DELETE_FACILITY'
      ),
      accept: () => {
        this.isLoading.set(true);
        this._commercialFacilityApi
          .deleteCommercialFacility(facility.id)
          .subscribe({
            next: () => {
              // The tab closes on success, so no local state cleanup is needed.
              this._commercialFacilityTableService.setSelected(null);
              this._logAndMessage.translateToSuccessMessage({
                bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
                headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              });
              if (this._deviceService.isMobile()) {
                const tab = this._tabService.buildNewTab(
                  COMMERCIAL_FACILITY_SEARCH_CONTAINER,
                  true
                );
                this._tabService.setMobileTab(tab);
              } else {
                this._tabService.closeTab(this._tabService.getActiveIndex());
              }
            },
            error: (error) => {
              console.error(error);
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                bodyKey:
                  'COMMERCIAL_SERVICES.MESSAGES.ERROR.DELETE_COMMERCIAL_FACILITY',
              });
              this.isLoading.set(false);
            },
            complete: () => this.isLoading.set(false),
          });
      },
    });
  }

  private showNoPermissionError(actionKey: string): void {
    this._logAndMessage.translateToErrorAlert({
      headerKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_HEADER',
      bodyKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_BODY',
      bodyParams: {
        action: this._translateService.instant(actionKey).toLowerCase(),
      },
    });
  }

  private handleLoadError(error: unknown): void {
    console.error(error);
    this._logAndMessage.translateToErrorAlert({
      headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      bodyKey: 'COMMERCIAL_SERVICES.MESSAGES.ERROR.GET_COMMERCIAL_FACILITY',
    });
    this.isLoading.set(false);
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
    this.tabClosingSub?.unsubscribe();
    this.facilitiesSub?.unsubscribe();
  }
}
